import { Component, computed, EventEmitter, inject, input, Input, output, Output } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CalendarEvent } from '@modules/calendar-app/types/calendar-event';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { type CalendarCellClickEvent } from '@modules/full-calendar/types/calendar-cell-click-event';
import { type CalendarDropEvent } from '@modules/full-calendar/types/calendar-drop-event';
import { CalendarEventSummaryDto } from '@sharedlib/dtos/dto/calendar-app/event.dto';
import { Sorter } from '@sharedlib/common/lang/Sorter';
import { type CalendarEvent as AngularCalendarEvent } from 'calendar-utils';

@Component({
  selector: 'stch-full-calendar-month',
  templateUrl: './full-calendar-month.component.html',
  styleUrls: ['./full-calendar-month.component.less'],
  standalone: false,
})
export class FullCalendarMonthComponent {
  private static readonly EVENTS_COMPARATOR = Sorter.byField<CalendarEventSummaryDto>('allDay').build(
    Sorter.byField('startTime').build(),
  );

  @Input() selectedDate: Date | null;

  @Input() viewDate: Date;

  public readonly events = input.required<CalendarEventSummaryDto[]>();

  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);

  protected readonly eventsChanged$ = toObservable<void>(this.events);

  protected readonly calendarEvents = computed<AngularCalendarEvent<CalendarEventSummaryDto>[]>(() => {
    // All-day events are positioned in the display zone (SBUX-829 H1); reactive to zone changes.
    const displayTimeZone = this.displayTimeZoneService.timeZone();
    return (this.events() || [])
      .sort(FullCalendarMonthComponent.EVENTS_COMPARATOR)
      .map((event) => CalendarEvent.makeAngularCalendarEvent(event, displayTimeZone));
  });

  public readonly scrollToDate = input<Date>();

  @Output() dateClicked = new EventEmitter<CalendarCellClickEvent>();
  @Output() dateDblClicked = new EventEmitter<Date>();
  @Output() eventDropped = new EventEmitter<CalendarDropEvent>();
  protected readonly eventClicked = output<CalendarEventSummaryDto>();
  protected readonly eventDblClicked = output<CalendarEventSummaryDto>();

  public size: 's' | 'm' | 'l';

  handleResize([width]: [number, number]) {
    if (width < 538) {
      this.size = 's';
    } else if (width < 800) {
      this.size = 'm';
    } else {
      this.size = 'l';
    }
  }
}
